import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { computeSettlementPayment } from "@torre/team";
import Modal from "../../../components/base/Modal";
import { useTheme } from "../../../context/ThemeContext";
import { buildThemeClasses, formatMoney, modeLabel } from "../theme";
import { assignSquad, fetchPayRates } from "../services/teamService";
import type { Squad, PayRate } from "../types";

// ---------------------------------------------------------------------------
// Asigna un squad completo a una tarea/tarea o release (AgroCloud#12): el squad
// y todos sus colaboradores se mueven juntos, que es lo que permite liquidar el
// trabajo por grupo. Se elige la tarifa del catálogo, se informa la cantidad que
// pide su modalidad (entregables, días, horas, % de avance o periodos, #530), y el
// total se previsualiza en vivo con la lógica
// compartida de `@torre/team` antes de confirmar contra el backend.
//
// Rescatado y adaptado del PR AgroCloud#98 (rama ai/issue-12-gestion-de-squads), que
// resolvía esto solo en el front con datos mock; aquí queda respaldado por el
// verbo `POST /squadAssign`, que crea la liquidación del squad.
// ---------------------------------------------------------------------------

interface AssignSquadModalProps {
  squad: Squad;
  onClose: () => void;
  onAssigned: () => void;
}

export default function AssignSquadModal({
  squad,
  onClose,
  onAssigned,
}: AssignSquadModalProps) {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);

  const [rates, setRates] = useState<PayRate[]>([]);
  const [rateId, setRateId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [date, setDate] = useState("");
  const [projectId, setProjectId] = useState(squad.projectId ?? "");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { items } = await fetchPayRates({ active: "true" });
        if (alive) setRates(items);
      } catch {
        if (alive) setError(t("team.loadError"));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [t]);

  const rate = useMemo(
    () => rates.find((x) => x.id === rateId),
    [rates, rateId],
  );

  // Qué cantidad pide la modalidad de la tarifa (#530).
  const QUANTITY_FIELD = {
    deliverable: "output",
    daily: "days",
    hourly: "hours",
    project: "progressPercentage",
    fixed_salary: "periods",
  } as const;
  const quantityField = QUANTITY_FIELD[rate?.mode ?? "deliverable"];
  const quantityInput = { [quantityField]: quantity === "" ? undefined : Number(quantity) };

  // Vista previa en vivo: misma función pura que usa el backend al persistir.
  const payment = useMemo(() => {
    if (!rate) return null;
    return computeSettlementPayment(rate, {
      [quantityField]: quantity === "" ? undefined : Number(quantity),
    });
  }, [rate, quantity, quantityField]);

  const isDeliverable = (rate?.mode ?? "deliverable") === "deliverable";
  const members = squad.members?.length ?? 0;

  const handleSubmit = async () => {
    if (!rate) {
      setError(t("team.errors.rateRequired"));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await assignSquad({
        squadId: squad.id,
        payRateId: rate.id,
        mode: rate.mode,
        ...quantityInput,
        date: date || null,
        projectId: projectId || null,
      });
      onAssigned();
      onClose();
    } catch {
      setError(t("team.errors.assignFailed"));
      setSubmitting(false);
    }
  };

  const inputBase = `w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors ${
    isDarkMode
      ? "bg-[#232323] border-dark-border text-white focus:border-primary-500"
      : "bg-white border-gray-300 text-gray-900 focus:border-primary-500"
  }`;

  return (
    <Modal
      title={t("team.assignTitle")}
      onClose={onClose}
      themeClasses={tc}
      width="max-w-xl"
      content={
        <div className="space-y-4">
          {/* Squad que se está asignando */}
          <div className={`rounded-xl border p-3 ${tc.border}`}>
            <p className={`font-semibold ${tc.text}`}>
              {squad.number ? `N.º ${squad.number} · ` : ""}
              {squad.name}
            </p>
            <p className={`text-xs ${tc.textSecondary}`}>
              {t("team.fields.lead")}: {squad.leadName ?? "—"} ·{" "}
              {t("team.membersCount", { count: members })}
            </p>
          </div>

          {loading ? (
            <p className={`text-sm ${tc.textSecondary}`}>
              <i className="ri-loader-4-line animate-spin mr-1" />
              {t("team.loading")}
            </p>
          ) : rates.length === 0 ? (
            <p className={`text-sm ${tc.textSecondary}`}>
              <i className="ri-information-line mr-1" />
              {t("team.noRates")}
            </p>
          ) : (
            <>
              {/* Tarifa a aplicar */}
              <div>
                <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>
                  {t("team.fields.work")}
                  <span className="ml-0.5 text-red-500">*</span>
                </label>
                <select
                  aria-label={t("team.fields.work")}
                  value={rateId}
                  onChange={(e) => {
                    setRateId(e.target.value);
                    setQuantity("");
                  }}
                  className={inputBase}
                >
                  <option value="">—</option>
                  {rates.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name} · {modeLabel(t, x.mode ?? "deliverable")}
                    </option>
                  ))}
                </select>
              </div>

              {/* Entregables (por entregable) o días (por día), según la tarifa */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>
                    {t(`team.fields.${quantityField}`)}
                    {isDeliverable && rate?.outputUnit ? ` (${rate.outputUnit})` : ""}
                  </label>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    className={inputBase}
                  />
                </div>
                <div>
                  <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>
                    {t("team.fields.date")}
                  </label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className={inputBase}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>
                    {t("team.fields.project")}
                  </label>
                  <input
                    value={projectId}
                    onChange={(e) => setProjectId(e.target.value)}
                    placeholder={t("team.hints.project")}
                    className={inputBase}
                  />
                </div>
              </div>

              {/* El pago no se puede calcular todavía: se explica por qué en
                  vez de mostrar un total de 0 para toda el squad. */}
              {payment?.noCalcReason && (
                <p className={`text-xs ${tc.textSecondary}`}>
                  <i className="ri-information-line mr-1" />
                  {t(`team.calc.noCalc.${payment.noCalcReason}`)}
                </p>
              )}

              {/* Vista previa del pago del squad */}
              {payment && !payment.noCalcReason && (
                <div className={`rounded-xl border p-3 ${tc.border} ${tc.cardBg}`}>
                  <div className="flex items-center justify-between text-xs">
                    <span className={tc.textSecondary}>
                      {t("team.calc.appliedPrice")}
                    </span>
                    <span className={tc.text}>{formatMoney(payment.appliedUnitPrice)}</span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-xs">
                    <span className={tc.textSecondary}>{t("team.calc.subtotal")}</span>
                    <span className={tc.text}>{formatMoney(payment.subtotal)}</span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-xs">
                    <span className={tc.textSecondary}>{t("team.calc.bonus")}</span>
                    <span className={tc.text}>{formatMoney(payment.bonus)}</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between border-t pt-2 text-sm font-semibold dark:border-dark-border">
                    <span className={tc.text}>{t("team.calc.total")}</span>
                    <span className="text-primary-500">{formatMoney(payment.total)}</span>
                  </div>
                  {members > 0 && payment.total !== null && (
                    <p className={`mt-1 text-[11px] ${tc.textSecondary}`}>
                      {t("team.calc.perMember", {
                        value: formatMoney(payment.total / members),
                      })}
                    </p>
                  )}
                </div>
              )}
            </>
          )}

          {error && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-500">
              <i className="ri-error-warning-line mr-1" />
              {error}
            </div>
          )}

          <div className="flex flex-col-reverse gap-2 border-t pt-4 dark:border-dark-border sm:flex-row sm:justify-end">
            <button
              onClick={onClose}
              className={`rounded-lg border px-4 py-2 text-sm ${tc.border} ${tc.text} ${tc.buttonHover}`}
            >
              {t("team.cancel")}
            </button>
            <button
              onClick={handleSubmit}
              // Sin un pago calculable el backend responde 422: no se ofrece
              // confirmar una asignación que no se puede pagar.
              disabled={submitting || !rateId || !payment || Boolean(payment.noCalcReason)}
              className="flex items-center justify-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-400 disabled:opacity-60"
            >
              <i className="ri-check-line" />
              {t("team.assign")}
            </button>
          </div>
        </div>
      }
    />
  );
}
